import { useMemo, useState } from "react";
import { Percent } from "lucide-react";
import "./CagrCalculator.css";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(n));

export default function CagrCalculator() {
  const [start, setStart] = useState(100000);
  const [end, setEnd] = useState(250000);
  const [years, setYears] = useState(5);

  const cagr = useMemo(() => {
    if (start <= 0 || end <= 0 || years <= 0) return 0;
    return (Math.pow(end / start, 1 / years) - 1) * 100;
  }, [start, end, years]);

  return (
    <section className="calc-card" id="cagr">
      <div className="calc-card__head">
        <div className="calc-card__icon">
          <Percent size={22} />
        </div>
        <div>
          <h2>CAGR Calculator</h2>
          <p>Measure annualised growth between a start and end value.</p>
        </div>
      </div>

      <div className="calc-layout">
        <div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="cagr-s">Starting value</label>
              <input
                id="cagr-s"
                className="calc-field__input"
                type="number"
                min={1}
                max={100000000}
                step={1000}
                value={start}
                onChange={(e) => setStart(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1000}
              max={5000000}
              step={1000}
              value={Math.min(Math.max(start, 1000), 5000000)}
              onChange={(e) => setStart(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="cagr-e">Ending value</label>
              <input
                id="cagr-e"
                className="calc-field__input"
                type="number"
                min={1}
                max={100000000}
                step={1000}
                value={end}
                onChange={(e) => setEnd(Number(e.target.value) || 0)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1000}
              max={10000000}
              step={1000}
              value={Math.min(Math.max(end, 1000), 10000000)}
              onChange={(e) => setEnd(Number(e.target.value))}
            />
          </div>
          <div className="calc-field">
            <div className="calc-field__top">
              <label htmlFor="cagr-y">Duration (years)</label>
              <input
                id="cagr-y"
                className="calc-field__input"
                type="number"
                min={1}
                max={40}
                step={0.5}
                value={years}
                onChange={(e) => setYears(Number(e.target.value) || 1)}
              />
            </div>
            <input
              className="calc-range"
              type="range"
              min={1}
              max={40}
              step={0.5}
              value={years}
              onChange={(e) => setYears(Number(e.target.value))}
            />
          </div>
        </div>

        <div className="calc-results">
          <div className="calc-result">
            <span>Start</span>
            <strong>{formatINR(start)}</strong>
          </div>
          <div className="calc-result">
            <span>End</span>
            <strong>{formatINR(end)}</strong>
          </div>
          <div className="calc-result calc-result--accent">
            <span>CAGR</span>
            <strong>{cagr.toFixed(2)}%</strong>
          </div>
          <p className="calc-note">
            CAGR = ((End / Start)^(1 / years) − 1) × 100
          </p>
        </div>
      </div>
    </section>
  );
}
